import React from "react";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Box,
  Avatar,
  Typography,
  Drawer,
  IconButton,
  Popover,
  MenuList,
  MenuItem,
} from "@mui/material";
import { X, Check } from "@phosphor-icons/react";
import anPurpleLogo from "../../assets/an_purple_butterfly_logo.png";
import { HeaderMainTitle } from "./Common/HeaderMainTitle";
import { DrawerMainContent } from "./Drawers/DrawerMainContent";
import { DrawerProfileContent } from "./Drawers/DrawerProfileContent";
import { DrawerClientProfileContent } from "./Drawers/DrawerClientProfileContent";
import { DrawerCareTeam } from "./Drawers/DrawerCareTeam";
import { DrawerDataPrivacy } from "./Drawers/DrawerDataPrivacy";
import { DrawerAssessments } from "./Drawers/DrawerAssessments";
import { DrawerDocuments } from "./Drawers/DrawerDocuments";
import { headerActions, getClientId } from "./utils";
import getAvatarInitials from "utils/getAvatarInitials";
import { CroppedAvatar } from "@answersnow/pkg-theme";
import { useGetTeams } from "hooks/api/useGetTeams";
import { useGetTeamMembers } from "hooks/api/useGetTeamMembers";
import { useGetClients } from "hooks/api/useGetClients";
import { useCustomerDetails } from "hooks/useCustomerDetails";
import cookie from "react-cookies";
import { addDays } from "date-fns";
import { getCookieDomain } from "utils/cookieDomain";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "../../selectors";
import actions from "../../actions";

type Views =
  | "main"
  | "profile"
  | "clientProfile"
  | "careTeam"
  | "dataPrivacy"
  | "assessments"
  | "documents";

export const ClientHeader: React.FC = () => {
  const { t } = useTranslation(["clientHeader"]);
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [currentView, setCurrentView] = React.useState<Views>("main");
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [animationDirection, setAnimationDirection] = React.useState<"left" | "right">("right");
  const [switchAccountsAnchor, setSwitchAccountsAnchor] = React.useState<HTMLElement | null>(null);
  const userId = user?.id;
  const { clients, clientsLoading } = useGetClients({ userIds: userId });
  const selectedClientId = getClientId(user);
  const clientId = selectedClientId;
  const { data: customerDetails } = useCustomerDetails(clientId);
  const { teams } = useGetTeams({ clientIds: clientId });
  const teamId = teams?.[0]?.id;
  const { teamMembers, teamMembersLoading } = useGetTeamMembers({
    teamIds: teamId,
    includeTeamInvitees: false,
  });
  const currentUserMember = teamMembers?.find((member) => member.userId === user?.id);
  const isDocumentSigner = currentUserMember?.documentSigner === true;
  const userFullName = user?.first_name ? `${user.first_name} ${user.last_name ?? ""}`.trim() : "";
  const userInitials = getAvatarInitials(userFullName);
  const avatarUrl = user?.imageUrl || user?.image_url;
  const hasMultipleClients = !clientsLoading && Array.isArray(clients) && clients.length > 1;

  const handleAvatarSuccess = (newImageUrl: string) => {
    const cacheBustedUrl = `${newImageUrl}?t=${Date.now()}`;
    dispatch(
      actions.getUserSuccess({
        ...user,
        image_url: cacheBustedUrl,
        imageUrl: cacheBustedUrl,
      })
    );
  };

  const toggleDrawer = () => {
    setOpenDrawer((o) => !o);
    if (!openDrawer) {
      setCurrentView("main");
      setIsAnimating(false);
    }
  };

  const navigateToView = (view: Views) => {
    if (view === currentView || isAnimating) return;

    const direction = view === "main" ? "left" : "right";
    setAnimationDirection(direction);
    setIsAnimating(true);

    if (view === "main") {
      window.setTimeout(() => {
        setCurrentView(view);
        setIsAnimating(false);
      }, 500);
    } else {
      setCurrentView(view);
      window.setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  };

  const handleSwitchAccountsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSwitchAccountsAnchor(event.currentTarget);
  };
  const handleSwitchAccountsClose = () => setSwitchAccountsAnchor(null);

  const onMenuItemClick = (clickHandler: () => void) => {
    setOpenDrawer(false);
    clickHandler();
  };

  const handleNavigateToProfile = () => navigateToView("profile");
  const handleNavigateToClientProfile = () => navigateToView("clientProfile");
  const handleNavigateToCareTeam = () => navigateToView("careTeam");
  const handleNavigateToDataPrivacy = () => navigateToView("dataPrivacy");
  const handleNavigateToAssessments = () => navigateToView("assessments");
  const handleNavigateToDocuments = () => navigateToView("documents");
  const handleBackToMain = () => navigateToView("main");

  const renderView = (view: Views) => {
    switch (view) {
      case "main":
        return (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "24px" }}>
              <IconButton
                onClick={toggleDrawer}
                sx={{ color: "#666" }}
                aria-label="Cerrar panel de cuenta"
              >
                <X size={24} />
              </IconButton>
            </Box>
            <HeaderMainTitle user={user} title={t("clientHeader:menu.account")} />
            <DrawerMainContent
              onMenuItemClick={onMenuItemClick}
              onNavigateToProfile={handleNavigateToProfile}
              onNavigateToClientProfile={handleNavigateToClientProfile}
              onNavigateToCareTeam={handleNavigateToCareTeam}
              onNavigateToDataPrivacy={handleNavigateToDataPrivacy}
              onNavigateToAssessments={handleNavigateToAssessments}
              onNavigateToDocuments={handleNavigateToDocuments}
              onSwitchAccountsClick={handleSwitchAccountsClick}
              user={user}
              isDocumentSigner={isDocumentSigner}
              hasMultipleClients={hasMultipleClients}
              customerDetails={customerDetails}
            />
            {typeof APP_VERSION !== "undefined" && (
              <Box sx={{ mt: "auto", p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  AnswersNow {new Date().getFullYear()} v{APP_VERSION}
                </Typography>
              </Box>
            )}
          </>
        );
      case "profile":
        return (
          <DrawerProfileContent
            onBack={handleBackToMain}
            user={user}
            currentUserMember={currentUserMember}
            isDocumentSigner={isDocumentSigner}
            onAvatarSuccess={handleAvatarSuccess}
          />
        );
      case "clientProfile":
        return (
          <DrawerClientProfileContent
            onBack={handleBackToMain}
            user={user}
            customerDetails={customerDetails}
          />
        );
      case "careTeam":
        return (
          <DrawerCareTeam
            onBack={handleBackToMain}
            user={user}
            teamMembers={teamMembers}
            teamMembersLoading={teamMembersLoading}
            customerDetails={customerDetails}
          />
        );
      case "dataPrivacy":
        return <DrawerDataPrivacy onBack={handleBackToMain} user={user} />;
      case "assessments":
        return <DrawerAssessments onBack={handleBackToMain} user={user} />;
      case "documents":
        return <DrawerDocuments onBack={handleBackToMain} user={user} />;
      default:
        return null;
    }
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{ backgroundColor: "#fff", height: "80px", justifyContent: "center" }}
      >
        <Toolbar
          disableGutters
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        >
          <Box>
            <img
              src={anPurpleLogo}
              alt="Answers Now"
              style={{ height: 29, width: "auto", cursor: "pointer" }}
              onClick={headerActions.navigation.dashboard}
            />
          </Box>

          <Box
            role="button"
            aria-label="Abrir menú de cuenta"
            onClick={toggleDrawer}
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <Typography sx={{ color: "#15171d", mr: 1 }}>{userFullName}</Typography>
            <CroppedAvatar
              src={avatarUrl}
              alt={userFullName}
              sx={{ width: 32, height: 32, backgroundColor: "#8E42CA", color: "#ffffff" }}
            >
              {userInitials}
            </CroppedAvatar>
          </Box>

          <Drawer
            anchor="right"
            open={openDrawer}
            onClose={toggleDrawer}
            variant="temporary"
            PaperProps={{
              sx: {
                width: "100%",
                maxWidth: 834,
                display: "flex",
                flexDirection: "column",
                borderRadius: "16px 0 0 16px",
                overflow: "hidden",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                }}
              >
                {renderView("main")}
              </Box>
              {currentView !== "main" && (
                <Box
                  key={`overlay-${currentView}`}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#fff",
                    overflowY: "auto",
                    animation: isAnimating
                      ? animationDirection === "right"
                        ? "slideInFromRight 500ms cubic-bezier(0.4, 0, 0.2, 1) forwards"
                        : "slideOutRight 500ms cubic-bezier(0.4, 0, 0.2, 1) forwards"
                      : "none",
                    "@keyframes slideInFromRight": {
                      from: { transform: "translateX(100%)" },
                      to: { transform: "translateX(0%)" },
                    },
                    "@keyframes slideOutRight": {
                      from: { transform: "translateX(0%)" },
                      to: { transform: "translateX(100%)" },
                    },
                  }}
                >
                  {renderView(currentView)}
                </Box>
              )}
            </Box>
          </Drawer>
        </Toolbar>
      </AppBar>

      <Popover
        anchorEl={switchAccountsAnchor}
        open={Boolean(switchAccountsAnchor)}
        onClose={handleSwitchAccountsClose}
        anchorOrigin={{ vertical: "center", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        PaperProps={{ style: { minWidth: 264, padding: 8 } }}
      >
        <MenuList>
          {!clientsLoading &&
            (clients || []).map((account: any) => {
              const safeInitial = (s?: string) => s?.[0]?.toUpperCase() ?? "";
              const accountInitials =
                `${safeInitial(account?.name)}${safeInitial(account?.child_last_name)}` || "•";
              const isSelected = selectedClientId === account.id;
              return (
                <MenuItem
                  key={account.id}
                  onClick={() => {
                    cookie.save(`${userId}-clientId`, account.id, {
                      path: "/",
                      domain: getCookieDomain(),
                      expires: addDays(new Date(), 30),
                    });
                    handleSwitchAccountsClose();
                    if (typeof window !== "undefined") window.location.reload();
                  }}
                >
                  <Avatar src={account?.avatarUrl} sx={{ mr: 1, width: 32, height: 32 }}>
                    {accountInitials}
                  </Avatar>
                  <Typography flexGrow={1} variant="body2">
                    {account?.name}
                  </Typography>
                  {isSelected && <Check size={24} color="#8E42CA" />}
                </MenuItem>
              );
            })}
        </MenuList>
      </Popover>
    </>
  );
};
